<template>
    <div>
        <div :class="styleClasses.wrapper" v-if="hasOptions">
            <label class="block text-body-6 font-medium text-default-1 text-left" :class="[props.labelPosition ? 'mb-2' : '']">
                {{ label }}
                <span v-if="required" class="text-interactive-3 text-body-4">*</span>
            </label>
            <div :class="[styleClasses.inputError, styleClasses.inputWidth]">

                <Variant
                    v-if="isDisabled"
                    :options="modelValue"
                    placeholder="Click to view selected options"
                    class="mb-6"
                    :is-disabled="true"
                />

                <Multiselect
                    v-else
                    ref="selectRef"
                    :modelValue="modelValue"
                    @update:modelValue="$emit('update:modelValue', $event)"
                    :options="options"
                    :placeholder="placeholder"
                    :label="labelProp"
                    :trackBy="trackByProp"
                    :valueProp="valueProp"
                    mode="tags"
                    :close-on-select="false"
                    :searchable="true"
                    :create-option="createOption"
                    :classes="classes"
                    @search-change="handleSearch"
                >
                    <template v-slot:beforelist="{ option }">
                        <div class="w-full uppercase text-body-7 text-default-3 px-4 mt-4" v-if="options.length > 0">SUGGESTED TAGS</div>
                        <div class="w-full" v-if="!createOption && options.length === 0">
                            <div class="w-full uppercase text-body-7 text-default-3 px-4 mt-4" v-if="options.length === 0">NO TAGS FOUND</div>
                        </div>
                        <div class="w-full" v-if="createOption && options.length === 0">
                            <div class="w-full uppercase text-body-7 text-default-3 px-4 mt-4" v-if="options.length === 0">PRESS ENTER TO CREATE TAG</div>
                        </div>
                    </template>

                    <template v-slot:option="{ option }" v-if="!loading">
                        <div :class="classes.tag" :style="{background: getAuxiliaryColor}" class="m-0 text-primary-1">
                            {{ option[props.labelProp] }}
                        </div>
                    </template>

                    <template v-slot:nooptions v-if="!loading">
                        <div :class="classes.noOptions">No tags available</div>
                    </template>

                    <template v-slot:noresults v-if="!loading">
                        <div :class="classes.noResults">No results found</div>
                    </template>

                    <template v-slot:afterlist>
                        <div class="w-full flex flex-row flex-wrap px-3" v-if="loading">
                            <div class="skeleton-loader w-8 h-5 rounded-3xl mt-1 ml-1" v-for="i in 24" :key="i"></div>
                        </div>
                        <InfiniteScrollObserver
                            v-if="!infiniteLoader && optionsPage < totalPages"
                            @intersect="checkInViewport">
                        </InfiniteScrollObserver>
                    </template>
                </Multiselect>
            </div>
        </div>
        <p v-if="hasError" class="text-body-7 font-medium mt-1" :class="styleClasses.errorMessage"> {{ hintText }} </p>
    </div>
</template>
<script setup>
import Multiselect from '@vueform/multiselect'

import {ref, computed, watch, defineProps, defineEmits} from 'vue'
import Variant from "@/components/core/Select/Variant.vue";
import {useTheme} from "@/stores/ThemeStore";
import InfiniteScrollObserver from "@/components/InfiniteScrollObserver.vue";
const {getAuxiliaryColor} = useTheme();

const optionsPage = ref(1);

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Search tags'
    },
    options: {
        type: Array,
        required: true,
        default: () => []
    },
    totalPages: {
        type: Number,
        required: true,
        default: 0
    },
    labelPosition: {
        type: String,
        validator: function (value) {
            return ['left', 'top'].indexOf(value) !== -1;
        },
        default: 'top',
    },
    modelValue: {
        required: true,
    },
    hintText: {
        type: String,
        default: null
    },
    hasError: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false,
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    trackByProp: {
        type: String,
        default: undefined
    },
    labelProp: {
        type: String,
        default: 'label'
    },
    valueProp: {
        type: String,
        default: 'name',
    },
    hasCheckbox: {
        type: Boolean,
        default: true,
    },
    isEditable: {
        type: Boolean,
        default: false,
    },
    closeOnSelect: {
        type: Boolean,
        default: false,
    },
    noResultsText: {
        type: String,
        default: 'No results found'
    },
    loading: {
        type: Boolean,
        default: false,
    },
    openDirection: {
        type: String,
        default: 'bottom'
    },
    createOption: {
        type: Boolean,
        default: false,
    },
    infiniteLoader: {
        type: Boolean,
        default: false,
    },
})

const selectRef = ref(null);

const emit = defineEmits(['update:modelValue', 'loadOptions', 'loadInfinite', 'searchOptions']);

const search = ref('');

function checkInViewport() {
    if (props.loading) {
        return;
    }

    optionsPage.value++
    emit('loadInfinite', {page: optionsPage.value, search: search.value});
}

function handleSearch(query) {
    if(query === search.value) {
        return;
    }

    emit('searchOptions', query);
    optionsPage.value = 1;
    search.value = query;
}

watch(props.options, () => {
    selectRef.value.refreshOptions();
}, {deep: true})

const hasOptions = computed(() => {
    return props.options != null
})

const styleClasses = computed(() => ({
    wrapper: {
        'flex items-center': props.labelPosition === 'left',
    },
    inputWidth: {
        'flex-1 ml-2': props.labelPosition === 'left',
    },
    inputError: {
        'border rounded-[6px] border-interactive-3 focus:border-interactive-3 hover:border-interactive-3 white-inset': props.hasError,
        'border-interactive-8 focus:border-primary-1 hover:border-primary-1': !props.hasError && !props.isDisabled && !props.isInline,
    },
    errorMessage: {
        'text-interactive-3': props.hasError,
        'text-gray-500': !props.hasError
    }
}));

const classes = ref({
    container: 'multiselect text-body-6 placeholder:text-default-4 bg-white border border-interactive-9 min-h-[40px] w-full rounded text-left focus:outline-none focus:ring-transparent focus:border-primary-1 hover:border hover:border-primary-1',
    search: 'w-full min-h-[40px] absolute border-0 inset-0 outline-none focus:ring-0 appearance-none box-border text-base font-sans bg-transparent rounded pl-3.5 rtl:pl-0 rtl:pr-3.5 hover:border-primary-1 hover:border',
    option: 'flex items-center justify-start box-border text-left cursor-pointer leading-snug pl-1 pr-1 py-1 focus:text-primary-1 text-body-6 text-default-1 first:ml-0',
    options: 'flex flex-row p-0 m-0 list-none flex-wrap px-3 pt-2 pb-4 box-border h-20',
    optionPointed: 'hover:text-primary-1 cursor-pointer',
    optionSelected: 'bg-white',
    optionDisabled: 'text-gray-300 cursor-not-allowed',
    optionSelectedPointed: 'bg-background-3 opacity-90',
    optionSelectedDisabled: 'text-green-100 bg-green-500 bg-opacity-50 cursor-not-allowed',
    multiselect: 'border border-primary-9',
    containerActive: !props.isInline && 'border-primary-1',
    tag: 'multiselect-tag capitalize font-normal py-0.5 px-1 box-border rounded-[6px] bg-primary-1 text-body-7',
    tags: 'multiselect-tags flex flex-wrap',
    tagsSearch: 'multiselect-tags-search p-0 text-body-7 text-default-1',
    noResults: 'multiselect-no-results text-body-6 text-default-2 px-4 box-border',
    noOptions: 'multiselect-no-options text-body-6 text-default-2 px-4 box-border',
})
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style lang="scss">
.multiselect {
    &.is-disabled {
        background: white;

        .multiselect-caret {
            display: none;
        }
    }
}

.multiselect-dropdown {
    max-height: 400px;
}

.multiselect-caret, .multiselect-clear-icon {
    z-index: 1;
}

.white-inset {
    -webkit-box-shadow: inset 0px 0px 0px 10px #FFFFFF;
    box-shadow: inset 0px 0px 0px 10px #FFFFFF;
}

.multiselect-tag.tag i:before {
    color: #ffffff;
    border-radius: 50%;;
}

.multiselect-tags-search, .multiselect-tags-search:focus {
    outline: none;
    border: 0;
    box-shadow: none;
}
</style>
